import { normalizedTranslate } from '@rbilabs/components-library';
import styled, { css, keyframes } from 'styled-components';

import { ActionButton } from 'components/action-button';
import { IconLocked } from 'components/icons/locked';
import { IconLoyaltyMyCode } from 'components/icons/loyalty-my-code';
import { IconLoyaltyPoints } from 'components/icons/loyalty-points-icon';
import { IconPhone } from 'components/icons/phone';
import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import { DisclaimerContainer } from 'components/offers/ui-refresh/offer-disclaimer';
import { Picture } from 'components/picture';
import { ProgressMeter } from 'components/progress-meter';
import { SanityBlockRenderer } from 'components/sanity-block-renderer';
import { UpcomingOfferBadge } from 'pages/loyalty/upcoming-offer/upcoming-offer-badge';
import { primitive } from 'styles/constants/primitives';
import { PointsDisplay, PointsDisplayIcon } from 'ui-system/components/points-display';
import { fadeIn } from 'utils/style/animations';

import { HIGHLIGHT_COLOR } from '../constants';
import { RewardLockIcon } from '../reward-lock-icon';

import { theme } from './theme';

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  flex-direction: column;
  animation: ${fadeIn} 0.65s forwards;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  width: 100vw;

  ${Styles.desktop} {
    width: 100%;
    animation: ${fadeIn} 0.25s forwards;
  }
`;

export const ImageWrapper = styled.div<{ hasMargin?: boolean }>`
  position: relative;
  height: fit-content;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-block-end: 0.75rem;
  flex-shrink: 0;
  margin-block-start: ${p => (p.hasMargin ? '4rem' : '0')};
`;

export const Padding = styled.div`
  min-height: 1.5rem;
  width: 1px;
`;

export const StyledRewardImage = styled(Picture)<{
  isChallengeIncentive?: boolean;
}>`
  width: 23.4375rem;
  height: 15.625rem;
  flex-shrink: 0;
  align-self: stretch;
  z-index: ${Styles.zIndex.normal};

  ${Styles.desktop} {
    height: fit-content;
    width: ${theme.rewardImageWidthDesktop};
  }

  ${p =>
    p.isChallengeIncentive &&
    css`
      opacity: 0.5;
    `}
`;

export const BackgroundImage = styled(Picture)<{ isChallengeIncentive?: boolean }>`
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: 0;
  width: 100%;
  height: 100%;
  ${p =>
    p.isChallengeIncentive &&
    css`
      opacity: 0.5;
    `}
`;

const slideRight = keyframes`
  from {
    transform: ${normalizedTranslate('50px')};
  }
  to {
    transform: ${normalizedTranslate(0)};
  }
`;

export const RewardName = styled.h2`
  font-size: 1.5rem;
  font-family: ${Styles.fontFamily.brand};
  line-height: 1.2;
  margin-block-start: 0.5rem;
  margin-block-end: 0;
  margin-inline: 0;
  text-align: center;
  max-width: 60rem;
  animation: ${slideRight} 0.5s forwards;

  ${Styles.desktop} {
    margin-block-start: 1rem;
    margin-block-end: 0;
    margin-inline: 0;
    animation: unset;
    font-size: 2.25rem;
  }
`;

export const RewardDescription = styled(SanityBlockRenderer)`
  margin-block-start: 0.5rem;
  margin-block-end: 0;
  margin-inline: 0;
  font-family: ${Styles.fontFamily.body};
  text-align: center;
  font-size: 0.812rem;
  line-height: 1.3rem;
  color: ${theme.rewardDescriptionColor};
  width: 85%;

  ${Styles.desktop} {
    font-size: 1rem;
    line-height: 1.4rem;
    max-width: 45rem;
  }
`;

export const ButtonContainer = styled.div<{ reversed?: boolean }>`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  ${p =>
    p.reversed &&
    css`
      flex-direction: column-reverse;
    `}

  ${Styles.desktop} {
    margin-block-start: 2rem;
    margin-block-end: 0;
    margin-inline: 0;
    width: auto;
  }

  > * {
    width: 100%;
  }
`;

export const RewardDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
`;

export const DisclaimerText = styled(SanityBlockRenderer)`
  max-width: 36rem;
  text-align: center;
  font-size: 0.75rem;
  font-family: ${Styles.fontFamily.body};
  font-style: normal;
  line-height: 1.33;
  color: ${theme.disclaimerTextColor};
  margin: 1rem auto;

  ${Styles.desktop} {
    margin-block-start: 2rem;
  }
`;

export const StyledProgressMeter = styled(ProgressMeter)`
  border: 0;
  background: ${primitive.$white};
  width: 15rem;
  margin: 0.5rem auto;
  outline: ${p => p.theme.cardOutline};

  ${Styles.desktop} {
    margin-block-start: 1.1rem;
  }
`;

export const PointsContainer = styled.div`
  display: flex;
  background: ${Styles.color.contrastBackground};
  color: ${primitive.$white};
  flex-direction: column;
  align-items: center;
  margin: 1rem 0;
  padding: 0.4em 0.6em;
  border-radius: 0.25rem;

  ${Styles.desktop} {
    margin-block-start: 1.5rem;
  }
`;

export const PointsText = styled.p`
  font-family: ${Styles.fontFamily.brand};
  font-size: 1.25rem;
  margin: 0;
  display: flex;
  align-items: center;

  strong {
    font-weight: ${Styles.fontWeight.heavy};
  }
`;

export const StyledPointsIcon = styled(IconLoyaltyPoints)`
  transform: scale(1.4);
  margin-block: 0;
  margin-inline-start: 0;
  margin-inline-end: 0.5rem;

  path {
    fill: ${Styles.color.success};
  }
`;

export const ErrorMessage = styled.p`
  text-align: center;
  color: ${Styles.color.error};
  padding: 0 1rem;
`;

export const RewardLabel = styled.div`
  background-color: ${HIGHLIGHT_COLOR};
  padding-block-start: 0.4rem;
  padding-block-end: 0.5rem;
  padding-inline: 0.6rem;
  border-radius: 0.5rem;
  color: ${Styles.color.white};
  margin-block-start: 0.3rem;
  margin-block-end: 0;
  margin-inline: 0.4rem;
  font-size: 0.9rem;
  line-height: 1;

  ${Styles.desktop} {
    display: none;
  }
`;

export const StyledRewardLockIcon = styled(RewardLockIcon)`
  & > div > div {
    background-color: ${HIGHLIGHT_COLOR};

    &::before,
    &::after {
      border-color: ${HIGHLIGHT_COLOR};
    }
  }
`;

export const RemoveRewardsButton = ActionButton;
export const OrderMoreButton = styled(ActionButton)`
  font-size: 1rem;
  padding: 0.75rem 2rem;
`;

export const StyledIncentiveEvaluationMessages = styled(IncentiveEvaluationMessages)`
  text-align: center;
`;

export const ButtonsWrapper = styled.div`
  position: sticky;
  inset-block-end: 0;
  background-color: ${p => p.theme.primaryBackground};
  display: flex;
  flex-flow: row wrap;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  z-index: ${Styles.zIndex.top};
  padding: 0 1rem;
  width: 100%;

  ${Styles.desktop} {
    inset-block-end: auto;
    position: static;
    background-color: transparent;
    width: auto;
    display: block;
    padding-block-start: 0;
    padding-block-end: 1rem;
    padding-inline: 1rem;
    z-index: auto;
  }
`;

export const StyledDisclaimerContainer = styled(DisclaimerContainer)`
  margin-block-start: 1rem;
  padding: 0 1.25rem;
`;

export const StyledActionButton = styled(ActionButton)<{ legacyStyle?: boolean }>`
  width: 100%;
  height: 2.5rem;
  font-size: 1rem !important;
  line-height: 1rem;

  ${p =>
    p.legacyStyle &&
    css`
      font-size: 1.125rem !important;
      line-height: 1.125rem;
      height: 2.875rem;
    `}

  ${Styles.desktop} {
    min-width: ${p => (p.legacyStyle ? 'unset' : '18.5rem')};
    margin: 0 auto;
  }
`;

export const StyledSecondaryActionButton = styled(ActionButton)<{ legacyStyle?: boolean }>`
  font-size: 1rem;
  height: 2.5rem;
  padding: 0.688rem 3rem;
  justify-content: center;
  width: 100%;
  line-height: 1rem;
  ${p =>
    p.disabled &&
    css`
      color: ${Styles.color.grey.two};
    `}

  ${p =>
    p.legacyStyle &&
    css`
      font-size: 1.25rem !important;
      line-height: 1.25rem;
      margin-block-end: 0;
      height: 3.125rem;
      color: ${Styles.color.highlight};
    `}

  ${Styles.desktop} {
    max-width: ${p => (p.legacyStyle ? 'unset' : '18.5rem')};
    margin: 0 auto;
  }
`;

export const StyledLoyaltyButtonText = styled.div`
  margin: auto 0;
  color: ${primitive.bk.$bbqBrown};
`;

export const StyledMobileOrderIcon = styled.div`
  background-color: ${primitive.bk.$flamingOrange};
  border-radius: 2.875rem;
  padding: 0.5rem;
  line-height: 0.625rem;
  width: 2.5rem;
  height: 2.5rem;

  && svg {
    margin-block-start: 0.063rem;
    stroke: transparent;
    fill: ${primitive.$white};
    width: 1.375rem;
    height: 1.375rem;
  }
`;

export const StyledInRestaurantIcon = styled.div`
  background-color: ${Styles.color.red};
  border-radius: 2.875rem;
  padding: 0.5rem;
  line-height: 0.625rem;
  width: 2.5rem;
  height: 2.5rem;

  && svg {
    stroke: transparent;
    width: 1.125rem;
    height: 1.125rem;
    margin-block-start: 0.125rem;
  }
`;

export const StyledLoyaltyButton = styled(ActionButton)`
  display: flex;
  padding: 0.5rem;
  gap: 1rem;
  background-color: ${primitive.$white};
  border-radius: 5rem;
  cursor: pointer;
  box-shadow: 0px 0px 6px 0px #0000001a;
  margin-block-end: 0.25rem;
  width: 100%;
  justify-content: start;

  &:hover {
    background-color: ${primitive.$white};
  }

  &:disabled {
    cursor: not-allowed;

    ${StyledLoyaltyButtonText} {
      color: ${primitive.$disabledText};
    }

    ${StyledMobileOrderIcon} {
      background-color: ${primitive.$disabledText};
    }

    ${StyledInRestaurantIcon} {
      background-color: ${primitive.$disabledText};
    }
  }

  ${Styles.desktop} {
    width: 22.5rem;
  }
`;

export const StyledLoyaltyButtonTitle = styled.div`
  text-align: start;
  font-weight: 400;
  font-size: 0.875rem;
`;

export const StyledLoyaltyButtonSubtitle = styled.div`
  text-align: start;
  font: var(--font-copyTwo);
`;

export const StyledActionNotifyMeButton = styled(ActionButton)`
  justify-content: center;
  color: ${Styles.color.red};
  background-color: ${Styles.color.background};
  border: 1px solid ${Styles.color.red};
  font-size: 1rem;
  width: 100%;

  &:hover {
    background-color: #f0cdbb;
  }
`;

export const WrapperButton = styled.div`
  position: absolute;
  z-index: 300;
  inset-inline-end: 0;
  margin-block-start: 0.8rem;
  margin-block-end: 0;
  margin-inline-start: 0;
  margin-inline-end: 0.8rem;
  padding: 0 0.3rem;
`;

export const LoadingWrapper = styled.div`
  height: 2.125rem;
`;

export const LockOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  background: ${primitive.$black};
  margin: 0;
  opacity: 0.5;
  z-index: 1000;
`;

export const RewardPictureLock = styled(IconLocked)`
  position: absolute;
  inset-block-start: 0;
  inset-block-end: 0;
  margin: auto;
  display: block;
  width: 2rem;
  height: 2rem;
  z-index: 1500;
`;

export const BarLockIcon = styled(IconLocked)`
  width: 2rem;

  ${Styles.desktop} {
    width: 3rem;
    height: 1.4rem;
    margin-block-end: -3px;
  }
`;

export const TierRequirementTag = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${primitive.$blackOpacity10};
  color: ${Styles.color.contrastBackground};
  width: fit-content;
  margin: 0.5rem 0;
  padding: 0.5rem;
  border-radius: 0.25rem;
`;

export const TierRequirementText = styled.span`
  color: ${primitive.bk.$bbqBrown};
  font-size: 0.9rem;
  line-height: 1rem;
  padding-inline-start: 0.25rem;
  font-weight: 400;
`;

export const TierLockedBar = styled.div`
  text-align: center;
  font-family: ${Styles.fontFamily.brand};
  font-size: 1.125rem;
  color: ${primitive.bk.$fieryRed};

  ${Styles.desktop} {
    font-size: 1.5rem;
  }
`;

export const StyledPointsDisplayTierLockedBar = styled(PointsDisplay)`
  gap: 0.5em;

  ${PointsDisplayIcon} {
    width: 1em;
    height: 1em;
  }
`;

export const TierLockedText = styled.div`
  font-family: ${Styles.fontFamily.body};
  color: ${primitive.bk.$bbqBrown};
  font-size: 0.85rem;
  margin-block-end: 2rem;
  padding: 0 2rem;

  ${Styles.desktop} {
    font-size: 0.95rem;
  }
`;

export const StyledUpcomingOfferBadge = styled(UpcomingOfferBadge)`
  margin-block-end: 0.5rem;
`;

export const StyledPointsDisplay = styled(PointsDisplay)`
  background: ${Styles.color.contrastBackground};
  color: ${primitive.$white};
  margin: 1rem 0;
  padding: 0.4em 0.55em;
  border-radius: 0.25rem;
  font-size: 1.25rem;
  font-family: ${Styles.fontFamily.brand};
  font-weight: normal;

  ${Styles.desktop} {
    margin-block-start: 1.5rem;
  }

  ${PointsDisplayIcon} {
    width: 1.4em;
    height: 1.4em;
  }
`;

export const LoyaltyFabContainer = styled.div`
  position: absolute;
  inset-block-end: 0;
  inset-inline-end: 0;
  margin-block-end: 3rem;
`;

export const StyledMyCodeIcon = styled(IconLoyaltyMyCode)`
  width: 0.8rem;
  height: 0.8rem;
  margin-block-end: 0.125rem;
`;

export const StyledPhoneIcon = styled(IconPhone)`
  width: 0.9rem;
  height: 0.9rem;
  margin-block-end: 0.125rem;
`;

export const OrderMoreToUnlockRewardButton = styled(StyledActionButton)<{ legacyStyle?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  width: auto;
`;

export const OrderMore = styled.div`
  text-align: center;
  font-family: ${Styles.fontFamily.brand};
  font-size: 1.125rem;
  margin: 1rem 0;

  ${Styles.desktop} {
    font-size: 1.5rem;
  }
`;

export const CrownMeterBar = styled(StyledProgressMeter)`
  outline: 0;
  background: rgba(80, 35, 20, 0.1);
`;
